import { ParlayLegResult, ProbUncertaintyBand } from '../services/api';
import { impliedProb, profitPer100 } from './parlay';

/** Markets the value panel understands. top3 and head-to-heads borrow the nearest backtested market's bands. */
export type BetMarket = 'win' | 'top3' | 'top5' | 'top10' | 'h2h';

const BAND_MARKET: Record<BetMarket, ProbUncertaintyBand['market']> = {
  win: 'win',
  top3: 'top5',
  top5: 'top5',
  top10: 'top10',
  h2h: 'top10',
};
const MIN_BUCKET_N = 50; // thinner buckets (e.g. top 5 at 60%+, 11 cases) borrow a neighbor's band
const DEFAULT_BAND_PTS = 3;

export const marketForTopN = (n: number): BetMarket => (n === 1 ? 'win' : n <= 3 ? 'top3' : n <= 5 ? 'top5' : 'top10');

/** +- uncertainty in percentage points for probability p in a market. */
export const bandFor = (bands: ProbUncertaintyBand[] | undefined, market: BetMarket, p: number): number => {
  const rows = (bands ?? []).filter((b) => b.market === BAND_MARKET[market] && b.n >= MIN_BUCKET_N);
  if (rows.length === 0) return DEFAULT_BAND_PTS;
  const inside = rows.find((b) => p >= b.pLo && p < b.pHi);
  if (inside) return inside.bandPts;
  // Outside the well-populated buckets: use the nearest one.
  const nearest = [...rows].sort(
    (a, b) => Math.abs((a.pLo + a.pHi) / 2 - p) - Math.abs((b.pLo + b.pHi) / 2 - p)
  )[0];
  return nearest.bandPts;
};

/**
 * Uncertainty of a parlay's joint probability, in points: each leg's relative model uncertainty combined (as if
 * independent) and scaled to the joint probability, plus the simulation's own Monte Carlo error.
 */
export const parlayBandPts = (
  bands: ProbUncertaintyBand[] | undefined,
  legs: ParlayLegResult[],
  jointProb: number,
  jointSE: number
): number => {
  let relSq = 0;
  legs.forEach((l) => {
    let market: BetMarket = 'h2h';
    let p = l.prob;
    if (l.kind === 'top_n') market = marketForTopN(l.n ?? 10);
    if (l.kind === 'not_top_n') {
      market = marketForTopN(l.n ?? 10);
      p = 1 - l.prob; // the band is symmetric in points: "not top N" is uncertain by as much as "top N"
    }
    const bandPts = bandFor(bands, market, p);
    if (l.prob > 0) relSq += (bandPts / 100 / l.prob) ** 2;
  });
  const modelPts = jointProb * Math.sqrt(relSq) * 100;
  return Math.sqrt(modelPts ** 2 + (jointSE * 100) ** 2);
};

/**
 * Uncertainty of P(at least one leg hits), in points. That's 1 - P(no leg hits): each leg's band applied to its miss
 * probability, combined as in parlayBandPts, plus the simulation's Monte Carlo error.
 */
export const anyBandPts = (
  bands: ProbUncertaintyBand[] | undefined,
  legs: ParlayLegResult[],
  anyProb: number,
  anySE: number
): number => {
  const asMisses = legs.map((l) => ({ ...l, kind: l.kind === 'top_n' ? 'not_top_n' : l.kind === 'not_top_n' ? 'top_n' : l.kind, prob: 1 - l.prob }));
  // parlayBandPts on the misses gives the band of P(no leg hits); P(any) = 1 - that has the same band in points.
  return parlayBandPts(bands, asMisses as ParlayLegResult[], 1 - anyProb, anySE);
};

export type EdgeConfidence = 'clear' | 'marginal' | 'within' | 'negative';

export interface BetValue {
  implied: number;
  edgePts: number; // our probability minus the book's implied, in points
  edgeRel: number; // our probability / implied - 1
  ev100: number; // expected profit per $100
  bandPts: number;
  confidence: EdgeConfidence;
  fullKelly: number; // fraction of bankroll
  stake: number; // suggested stake as a fraction of bankroll (fractional Kelly, capped; 0 unless the edge is real)
}

export const MAX_STAKE = 0.05; // never suggest more than 5% of bankroll on one bet
export const MAX_MARGINAL_STAKE = 0.02; // marginal edges: half the fractional-Kelly stake, at most 2%

export const evaluateBet = (p: number, odds: number, bandPts: number, kellyFraction: number): BetValue => {
  const implied = impliedProb(odds);
  const edgePts = (p - implied) * 100;
  const b = profitPer100(odds) / 100;
  const fullKelly = Math.max(0, (b * p - (1 - p)) / b);
  const ratio = bandPts > 0 ? edgePts / bandPts : Infinity;
  const confidence: EdgeConfidence =
    edgePts <= 0 ? 'negative' : ratio > 2 ? 'clear' : ratio > 1 ? 'marginal' : 'within';
  // Clear edges get the fractional-Kelly stake (capped); marginal ones half of it with a lower cap; none otherwise.
  const stake =
    confidence === 'clear'
      ? Math.min(MAX_STAKE, fullKelly * kellyFraction)
      : confidence === 'marginal'
        ? Math.min(MAX_MARGINAL_STAKE, fullKelly * kellyFraction * 0.5)
        : 0;
  return {
    implied,
    edgePts,
    edgeRel: implied > 0 ? p / implied - 1 : 0,
    ev100: p * profitPer100(odds) - (1 - p) * 100,
    bandPts,
    confidence,
    fullKelly,
    stake,
  };
};

export const CONFIDENCE_LABEL: Record<EdgeConfidence, string> = {
  clear: 'Clear edge',
  marginal: 'Marginal edge',
  within: 'Within model uncertainty',
  negative: 'No edge',
};

/** One-line plain-language verdict. */
export const verdict = (v: BetValue): string => {
  switch (v.confidence) {
    case 'negative':
      return 'Priced at or below our fair odds: negative expected value. Pass.';
    case 'within':
      return `Positive EV on paper, but the edge (${v.edgePts.toFixed(1)} pts) is within our uncertainty at this price (±${v.bandPts.toFixed(1)}). Pass, or treat as a coin flip on the model.`;
    case 'marginal':
      return `Edge of ${v.edgePts.toFixed(1)} pts is beyond our uncertainty (±${v.bandPts.toFixed(1)}), but not by much. Small stake at most.`;
    case 'clear':
      return `Edge of ${v.edgePts.toFixed(1)} pts is more than twice our uncertainty (±${v.bandPts.toFixed(1)}). Worth a bet at the suggested size.`;
  }
};

/**
 * Expected bankroll growth from betting the suggested stake, in basis points of bankroll per bet (log growth x 10,000).
 * It is judged on a cautious probability (ours minus the margin of error), so an edge inside our uncertainty grows
 * nothing, and it weighs the edge against how often the bet hits: a long losing run between rare wins costs growth
 * that a likely bet with the same edge doesn't. Used to rank bets, so thin longshot edges don't crowd out solid
 * edges on likely bets.
 */
export const growthBps = (p: number, odds: number, bandPts: number, kellyFraction = 0.25): number => {
  const pc = p - bandPts / 100;
  const b = profitPer100(odds) / 100;
  if (!(pc > 0) || !(b > 0)) return 0;
  const fullKelly = (b * pc - (1 - pc)) / b;
  if (fullKelly <= 0) return 0;
  const f = Math.min(MAX_STAKE, fullKelly * kellyFraction);
  return (pc * Math.log(1 + f * b) + (1 - pc) * Math.log(1 - f)) * 10000;
};

/** How often a bet hits, in plain words: "7 in 10" for likely bets, "1 in 14" for unlikely ones. */
export const hitsLabel = (p: number): string => {
  if (!(p > 0)) return 'never';
  if (p >= 0.5) return `${Math.min(10, Math.round(p * 10))} in 10`;
  return `1 in ${Math.max(2, Math.round(1 / p))}`;
};
